import { ReadProjectSettlements } from "@/schemas/projectSettlements";
import { BreadcrumbItem } from "@/types/breadcrumb";
import { UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSettlementsDetailsBreadcrumb } from "@/modules/projects/components/settlementsDetails/hooks/useSettlementsDetailsBreadcrumb";
import { ReadInvoiceSpecification } from "@/types/invoiceSpecification";

interface InvoiceSpecificationsBreadcrumbsEditorProps {
  projectId: string | undefined;
  settlementId: string | undefined;
  invoiceSpecificationQuery: UseQueryResult<ReadInvoiceSpecification>;
}

export const useInvoiceSpecificationsEditorBreadcrumbs = ({
  projectId,
  settlementId,
  invoiceSpecificationQuery,
}: InvoiceSpecificationsBreadcrumbsEditorProps): BreadcrumbItem[] => {
  const { t } = useTranslation();
  // Create a mock settlement query from the invoice specification data
  const settlementQuery =
    useMemo((): UseQueryResult<ReadProjectSettlements> => {
      return {
        data: invoiceSpecificationQuery.data
          ? ({
              id: invoiceSpecificationQuery.data.period_id,
              period: invoiceSpecificationQuery.data.period,
            } as ReadProjectSettlements)
          : undefined,
        isLoading: invoiceSpecificationQuery.isLoading,
        error: invoiceSpecificationQuery.error,
      } as UseQueryResult<ReadProjectSettlements>;
    }, [invoiceSpecificationQuery]);

  // Get the settlements breadcrumb
  const settlementsBreadcrumbs = useSettlementsDetailsBreadcrumb({
    projectId,
    settlementId,
    settlementQuery,
  });

  // Create the invoice specification breadcrumb item
  const invoiceSpecificationBreadcrumbItem = useMemo(() => {
    return {
      label: t("projectInvoiceSpecifications.title.edit"),
    };
  }, [t]);

  // Append the invoice specification breadcrumb to the settlements breadcrumbs
  return [...settlementsBreadcrumbs, invoiceSpecificationBreadcrumbItem];
};
